import axios from "axios";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { Device } from "@twilio/voice-sdk";
import config from "@/config/constants";

// Module-level Twilio Device + active connection (outside Vuex state to avoid reactivity issues)
let twilioDevice = null;
let activeConnection = null;
let reconnectAttempts = 0;
let reconnectTimer = null;
const MAX_RECONNECT_ATTEMPTS = 3;

// Cleanup on tab close / navigation away
window.addEventListener('beforeunload', () => {
  if (activeConnection) {
    activeConnection.disconnect();
    activeConnection = null;
  }
  if (twilioDevice) {
    twilioDevice.destroy();
    twilioDevice = null;
  }
});

export default {
  namespaced: true,
  state: {
    // Current call state
    activeCall: null,       // { call_id, type, status, caller, callee, chat_id, ... }
    callToken: null,        // Twilio access token
    callStatus: 'idle',     // idle | ringing | active | voicemail | ended
    isMuted: false,
    isSpeakerOn: false,

    // Incoming call
    incomingCall: null,     // signal doc data from call_signals listener

    // Call history
    callHistory: [],
    callHistoryLoading: false,

    // Listeners
    _signalUnsubscribe: null,
  },
  mutations: {
    SET_ACTIVE_CALL(state, call) {
      state.activeCall = call;
    },
    SET_CALL_TOKEN(state, token) {
      state.callToken = token;
    },
    SET_CALL_STATUS(state, status) {
      state.callStatus = status;
    },
    SET_MUTED(state, val) {
      state.isMuted = val;
    },
    SET_SPEAKER(state, val) {
      state.isSpeakerOn = val;
    },
    SET_INCOMING_CALL(state, call) {
      state.incomingCall = call;
    },
    SET_CALL_HISTORY(state, calls) {
      state.callHistory = calls;
    },
    SET_CALL_HISTORY_LOADING(state, val) {
      state.callHistoryLoading = val;
    },
    SET_SIGNAL_UNSUBSCRIBE(state, fn) {
      state._signalUnsubscribe = fn;
    },
    RESET_CALL(state) {
      state.activeCall = null;
      state.callToken = null;
      state.callStatus = 'idle';
      state.isMuted = false;
      state.isSpeakerOn = false;
      state.incomingCall = null;
    },
  },
  actions: {
    /**
     * Initialize Twilio Device with a token
     */
    _initDevice({ dispatch }, token) {
      // Destroy existing device
      if (twilioDevice) {
        twilioDevice.destroy();
        twilioDevice = null;
      }

      twilioDevice = new Device(token, {
        codecPreferences: ['opus', 'pcmu'],
        closeProtection: true,
        edge: 'roaming',
        enableDscp: true,
      });

      twilioDevice.on('error', (err) => {
        console.log('[Twilio Device] Error:', err.message);
      });

      twilioDevice.on('tokenWillExpire', () => {
        dispatch('refreshToken');
      });

      twilioDevice.register();
    },

    /**
     * Initiate an outgoing call
     */
    async initiateCall({ commit, dispatch, state, rootState }, { calleeId, chatId, callType }) {
      if (state.callStatus !== 'idle') {
        console.log('[CallStore] Already in a call, ignoring initiateCall');
        return;
      }
      const token = rootState.Authenticate.token;
      commit('SET_CALL_STATUS', 'ringing');

      try {
        const response = await axios.post(
          `${config.api_url}calls/initiate`,
          {
            callee_id: calleeId,
            chat_id: chatId,
            call_type: callType,
          },
          { headers: { Authorization: token } }
        );

        if (response.data.code === 'SUCCESS') {
          const data = response.data.data;
          commit('SET_ACTIVE_CALL', data.call);
          commit('SET_CALL_TOKEN', data.token);

          // Initialize Twilio Device and connect
          dispatch('_initDevice', data.token);

          // Connect the call — Twilio routes via TwiML app to the callee
          activeConnection = await twilioDevice.connect({
            params: {
              To: calleeId,
            },
            rtcConstraints: {
              audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
              },
            },
          });

          // Attach connection event listeners (reused for reconnected connections)
          const audioConstraints = {
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          };

          const attachConnectionListeners = (conn) => {
            conn.on('accept', () => {
              commit('SET_CALL_STATUS', 'active');
              reconnectAttempts = 0;
            });

            conn.on('disconnect', () => {
              // In voicemail mode, server handles finalization — just reset client state
              if (state.callStatus === 'voicemail') {
                console.log('[Twilio Call] Disconnect during voicemail — server handles finalization');
                reconnectAttempts = 0;
                activeConnection = null;
                if (twilioDevice) { twilioDevice.destroy(); twilioDevice = null; }
                commit('RESET_CALL');
                return;
              }
              // If call was active and we haven't exhausted retries, attempt reconnect
              if (state.callStatus === 'active' && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
                reconnectAttempts++;
                console.log(`[Twilio Call] Disconnected, reconnect attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS}`);
                commit('SET_CALL_STATUS', 'reconnecting');
                const delay = Math.min(1000 * Math.pow(2, reconnectAttempts - 1), 4000);
                reconnectTimer = setTimeout(() => {
                  reconnectTimer = null;
                  if (state.callStatus !== 'reconnecting' || !twilioDevice) return;
                  twilioDevice.connect({
                    params: { To: calleeId },
                    rtcConstraints: audioConstraints,
                  }).then((newConn) => {
                    activeConnection = newConn;
                    attachConnectionListeners(newConn);
                  }).catch(() => {
                    dispatch('endCall');
                  });
                }, delay);
              } else {
                dispatch('endCall');
              }
            });

            conn.on('cancel', () => {
              reconnectAttempts = 0;
              commit('RESET_CALL');
              activeConnection = null;
            });

            conn.on('error', (err) => {
              console.log('[Twilio Call] Error:', err.message);
              reconnectAttempts = 0;
              commit('RESET_CALL');
              activeConnection = null;
            });
          };

          attachConnectionListeners(activeConnection);

          return data;
        }
      } catch (err) {
        commit('SET_CALL_STATUS', 'idle');
        console.log('[Call] Initiate error:', err.message);
        throw err;
      }
    },

    /**
     * Answer an incoming call
     */
    async answerCall({ commit, dispatch, rootState }, { callId }) {
      const token = rootState.Authenticate.token;

      try {
        const response = await axios.post(
          `${config.api_url}calls/answer`,
          { call_id: callId },
          { headers: { Authorization: token } }
        );

        if (response.data.code === 'SUCCESS') {
          const data = response.data.data;
          commit('SET_CALL_TOKEN', data.token);
          commit('SET_INCOMING_CALL', null);
          commit('SET_CALL_STATUS', 'active');

          // Initialize Twilio Device for receiving
          dispatch('_initDevice', data.token);

          // If there's a pending incoming Twilio call, accept it
          // For web, the connection comes through the Device's incoming event
          // which we set up below. The callee connects by registering the device.

          return data;
        }
      } catch (err) {
        commit('SET_CALL_STATUS', 'idle');
        console.log('[Call] Answer error:', err.message);
        throw err;
      }
    },

    /**
     * End an active call
     */
    async endCall({ commit, state, rootState }) {
      if (!state.activeCall) return;

      const isVoicemail = state.callStatus === 'voicemail';

      // Cancel any pending reconnect
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      reconnectAttempts = 0;

      // Disconnect Twilio connection
      if (activeConnection) {
        activeConnection.disconnect();
        activeConnection = null;
      }
      if (twilioDevice) {
        twilioDevice.destroy();
        twilioDevice = null;
      }

      // In voicemail mode, the server handles finalization via Twilio recording callback
      if (isVoicemail) {
        commit('RESET_CALL');
        return;
      }

      const token = rootState.Authenticate.token;
      const callId = state.activeCall.call_id;

      try {
        const response = await axios.post(
          `${config.api_url}calls/end`,
          { call_id: callId },
          { headers: { Authorization: token } }
        );

        commit('RESET_CALL');
        return response.data.data;
      } catch (err) {
        commit('RESET_CALL');
        console.log('[Call] End error:', err.message);
      }
    },

    /**
     * Decline an incoming call
     */
    async declineCall({ commit, rootState }, { callId }) {
      const token = rootState.Authenticate.token;

      // Reject Twilio incoming call if any
      if (activeConnection) {
        activeConnection.reject();
        activeConnection = null;
      }

      try {
        await axios.post(
          `${config.api_url}calls/decline`,
          { call_id: callId },
          { headers: { Authorization: token } }
        );
      } catch (err) {
        console.log('[Call] Decline error:', err.message);
      }

      commit('SET_INCOMING_CALL', null);
      commit('RESET_CALL');
    },

    /**
     * Mark call as missed (called after 30s timeout on caller side)
     */
    async markMissed({ commit, rootState }, { callId }) {
      const token = rootState.Authenticate.token;

      // Cancel any pending reconnect
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      reconnectAttempts = 0;

      if (activeConnection) {
        activeConnection.disconnect();
        activeConnection = null;
      }
      if (twilioDevice) {
        twilioDevice.destroy();
        twilioDevice = null;
      }

      try {
        await axios.post(
          `${config.api_url}calls/missed`,
          { call_id: callId },
          { headers: { Authorization: token } }
        );
      } catch (err) {
        console.log('[Call] Missed error:', err.message);
      }

      commit('RESET_CALL');
    },

    /**
     * Refresh Twilio token during long calls
     */
    async refreshToken({ commit, dispatch, state, rootState }) {
      if (!state.activeCall) return;

      const token = rootState.Authenticate.token;

      try {
        const response = await axios.post(
          `${config.api_url}calls/token`,
          { call_id: state.activeCall.call_id },
          { headers: { Authorization: token } }
        );

        if (response.data.code === 'SUCCESS') {
          const newToken = response.data.data.token;
          commit('SET_CALL_TOKEN', newToken);

          // Update Twilio Device token
          if (twilioDevice) {
            twilioDevice.updateToken(newToken);
          }

          return newToken;
        }
      } catch (err) {
        console.log('[Call] Token refresh error:', err.message);
        // Token refresh failed — Twilio will drop audio silently. End the call.
        dispatch('endCall');
      }
    },

    /**
     * Fetch call history
     */
    async fetchCallHistory({ commit, rootState }, { limit } = {}) {
      const token = rootState.Authenticate.token;
      commit('SET_CALL_HISTORY_LOADING', true);

      try {
        const response = await axios.get(
          `${config.api_url}calls/history`,
          {
            params: { limit: limit || 50 },
            headers: { Authorization: token },
          }
        );

        if (response.data.code === 'SUCCESS') {
          commit('SET_CALL_HISTORY', response.data.data.calls);
        }
      } catch (err) {
        console.log('[Call] History error:', err.message);
      }

      commit('SET_CALL_HISTORY_LOADING', false);
    },

    /**
     * Listen for incoming call signals via Firestore onSnapshot.
     * Web clients use this instead of VoIP push.
     */
    startCallSignalListener({ commit, rootState }) {
      const userId = rootState.Authenticate.owner_id;
      if (!userId) return;

      const unsubscribe = firebase.firestore()
        .collection('call_signals')
        .doc(userId)
        .collection('pending')
        .onSnapshot((snapshot) => {
          snapshot.docChanges().forEach((change) => {
            if (change.type === 'added') {
              // Ignore if already in a call or ringing
              if (rootState.Call?.callStatus !== 'idle') {
                console.log('[CallStore] Already in a call, ignoring incoming signal');
                return;
              }
              const signalData = change.doc.data();
              signalData.call_id = change.doc.id;
              commit('SET_INCOMING_CALL', signalData);
              commit('SET_CALL_STATUS', 'ringing');

              // Build activeCall shape from signal data
              commit('SET_ACTIVE_CALL', {
                call_id: signalData.call_id,
                type: signalData.call_type,
                status: 'ringing',
                caller_id: signalData.caller_id,
                caller: {
                  name: signalData.caller_name,
                  avatar: signalData.caller_avatar,
                },
                chat_id: signalData.chat_id,
              });
            }

            if (change.type === 'removed') {
              // Signal removed = call was answered/declined/missed elsewhere
              const removedId = change.doc.id;
              const currentIncoming = rootState.Call?.incomingCall;
              if (currentIncoming && currentIncoming.call_id === removedId) {
                commit('SET_INCOMING_CALL', null);
                // Only reset if we haven't already answered
                if (rootState.Call?.callStatus === 'ringing') {
                  commit('RESET_CALL');
                }
              }
            }
          });
        }, error => {
          console.error('[CallStore] Call signal listener error:', error);
        });

      commit('SET_SIGNAL_UNSUBSCRIBE', unsubscribe);
    },

    /**
     * Stop listening for call signals
     */
    stopCallSignalListener({ state, commit }) {
      if (state._signalUnsubscribe) {
        state._signalUnsubscribe();
        commit('SET_SIGNAL_UNSUBSCRIBE', null);
      }
    },

    /**
     * Full cleanup — call on logout or session end.
     * Tears down Twilio device, active connection, and signal listener.
     */
    cleanup({ dispatch, commit }) {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      reconnectAttempts = 0;
      if (activeConnection) {
        activeConnection.disconnect();
        activeConnection = null;
      }
      if (twilioDevice) {
        twilioDevice.destroy();
        twilioDevice = null;
      }
      dispatch('stopCallSignalListener');
      commit('RESET_CALL');
    },

    /**
     * Toggle mute — controls actual Twilio audio
     */
    toggleMute({ commit, state }) {
      const newMuted = !state.isMuted;
      if (activeConnection) {
        activeConnection.mute(newMuted);
      }
      commit('SET_MUTED', newMuted);
    },

    /**
     * Toggle speaker
     */
    toggleSpeaker({ commit, state }) {
      const newSpeaker = !state.isSpeakerOn;
      // Web browsers handle speaker via system audio output — Twilio SDK
      // uses default audio device. setSinkId can be used if needed.
      if (twilioDevice && twilioDevice.audio) {
        // Toggle between default and speaker output if available
        const audioHelper = twilioDevice.audio;
        if (newSpeaker && audioHelper.speakerDevices) {
          audioHelper.speakerDevices.set('default');
        }
      }
      commit('SET_SPEAKER', newSpeaker);
    },
  },
  getters: {
    isInCall: (state) => state.callStatus === 'active' || state.callStatus === 'voicemail',
    isRinging: (state) => state.callStatus === 'ringing',
    hasIncomingCall: (state) => state.incomingCall !== null,
  },
};
